import React, { useState, useEffect, useCallback } from 'react';
import './ManageStudentAssignments.css';
import '../TeacherDashboard/MyStudents.css';
import collegeConfig from '../../config/collegeConfig';
import {
  FaGraduationCap,
  FaSearch,
  FaTimes,
  FaSave,
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaUserGraduate,
} from 'react-icons/fa';

const personName = (p) =>
  `${p.profile?.firstName || ''} ${p.profile?.lastName || ''}`.trim() || p.email || '?';

const personInitials = (p) => {
  const f = p.profile?.firstName?.[0] || '';
  const l = p.profile?.lastName?.[0] || '';
  return (f + l).toUpperCase() || '?';
};

const fmt = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const TeacherGrid = ({ allTeachers, selectedIds, onToggle }) => {
  const [search, setSearch] = useState('');

  const filtered = allTeachers
    .filter((t) => !search || personName(t).toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => personName(a).localeCompare(personName(b)));

  return (
    <div className="ms-student-grid-wrapper">
      <div className="ms-grid-toolbar">
        <div className="ms-search-box">
          <FaSearch className="ms-search-icon" />
          <input
            type="text"
            placeholder="Search faculty..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="ms-search-input"
          />
          {search && (
            <button className="ms-search-clear" onClick={() => setSearch('')}>
              <FaTimes />
            </button>
          )}
        </div>
        <span className="ms-count-label">{selectedIds.length} selected</span>
      </div>

      {filtered.length === 0 ? (
        <p className="ms-no-results">No faculty match your search.</p>
      ) : (
        <div className="ms-student-grid">
          {filtered.map((t) => {
            const id = String(t._id || t.id);
            const selected = selectedIds.includes(id);
            const hasLeft = t.leavingDate && new Date(t.leavingDate) < new Date();
            return (
              <div
                key={id}
                className={`ms-student-card ${selected ? 'selected' : ''}`}
                onClick={() => onToggle(id)}
              >
                <div className="ms-student-avatar">{personInitials(t)}</div>
                <div className="ms-student-body">
                  <span className="ms-student-name">{personName(t)}</span>
                  {t.profile?.department && (
                    <span className="ms-student-reg">{t.profile.department}</span>
                  )}
                  {t.isActive === false && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#b45309' }}>Inactive</span>
                  )}
                  {t.leavingDate && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#b45309' }}>
                      {hasLeft ? 'Left' : 'Leaving'} {fmt(t.leavingDate)}
                    </span>
                  )}
                </div>
                <div className="ms-student-check">
                  {selected ? (
                    <FaCheckCircle className="ms-check-icon selected" />
                  ) : (
                    <div className="ms-check-circle" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ManageThesisFaculty = () => {
  const token = localStorage.getItem('token');

  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [selectedCourseId, setSelectedCourseId] = useState('');

  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [assignments, setAssignments] = useState({}); // { studentId: string[] }
  const [detailLoading, setDetailLoading] = useState(false);

  const [expandedStudentId, setExpandedStudentId] = useState(null);
  const [savingStudentId, setSavingStudentId] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState('');
  const [error, setError] = useState('');

  const selectedCourse = courses.find((c) => c._id === selectedCourseId);

  const showSuccess = (msg) => {
    setSaveSuccess(msg);
    setTimeout(() => setSaveSuccess(''), 3000);
  };

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setCoursesLoading(true);
        const res = await fetch(`${collegeConfig.api.baseURL}/courses`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok) {
          setCourses(data.courses || []);
        } else {
          setError(data.message || 'Failed to load departments');
        }
      } catch (err) {
        console.error('Error fetching courses:', err);
        setError('Failed to load departments');
      } finally {
        setCoursesLoading(false);
      }
    };
    fetchCourses();
  }, [token]);

  const fetchDetail = useCallback(async () => {
    if (!selectedCourseId) return;
    setDetailLoading(true);
    setError('');
    setExpandedStudentId(null);
    try {
      const [studentsRes, teachersRes] = await Promise.all([
        fetch(`${collegeConfig.api.baseURL}/dissertation/students?courseId=${selectedCourseId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${collegeConfig.api.baseURL}/dissertation/admin/teachers?courseId=${selectedCourseId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      const studentsData = await studentsRes.json();
      const teachersData = await teachersRes.json();

      if (studentsRes.ok && studentsData.success) {
        setStudents(studentsData.students || []);
        const map = {};
        (studentsData.students || []).forEach((s) => {
          map[String(s._id)] = (s.dissertation?.pgTeacherIds || []).map((t) => String(t._id || t));
        });
        setAssignments(map);
      } else {
        setError(studentsData.message || 'Failed to load students for this department');
      }

      if (teachersRes.ok && teachersData.success) {
        setTeachers(teachersData.teachers || []);
      } else {
        setError(teachersData.message || 'Failed to load faculty for this department');
      }
    } catch (err) {
      console.error('Error fetching thesis faculty detail:', err);
      setError('Failed to load department data');
    } finally {
      setDetailLoading(false);
    }
  }, [selectedCourseId, token]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const toggle = (studentId, teacherId) => {
    setAssignments((prev) => {
      const cur = prev[studentId] || [];
      return {
        ...prev,
        [studentId]: cur.includes(teacherId)
          ? cur.filter((id) => id !== teacherId)
          : [...cur, teacherId]
      };
    });
  };

  const saveOneStudent = async (studentId) => {
    setSavingStudentId(studentId);
    try {
      const res = await fetch(
        `${collegeConfig.api.baseURL}/dissertation/admin/assign/${studentId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ courseId: selectedCourseId, pgTeacherIds: assignments[studentId] || [] })
        }
      );
      const data = await res.json();
      if (res.ok && data.success) {
        showSuccess('Saved');
        setStudents((prev) => prev.map((s) => (String(s._id) === studentId ? { ...s, dissertation: data.dissertation } : s)));
      } else {
        setError(data.message || 'Failed to save assignment');
      }
    } catch (err) {
      console.error('Error saving thesis faculty assignment:', err);
      setError('Failed to save assignment');
    } finally {
      setSavingStudentId(null);
    }
  };

  return (
    <div className="msa-container">
      <div className="msa-header">
        <h2><FaGraduationCap style={{ marginRight: 10 }} />Manage Thesis Faculty</h2>
        <p className="msa-subtitle">
          Assign thesis/dissertation guide(s) to each student. This only takes effect while "Admin Assigns Faculty" is
          active in System Settings — students will see this list but cannot change it.
        </p>
        <p className="msa-subtitle">
          <strong>Handing off a thesis:</strong> when a guide leaves, set their Leaving Date in Manage Teachers, then
          just tick the replacement faculty here without unticking the departing one — their name will keep showing
          on the student's thesis with a "signed till {'{'}date{'}'}" note.
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="msa-department-picker">
        <label htmlFor="mtf-dept-select">Department</label>
        <select
          id="mtf-dept-select"
          value={selectedCourseId}
          onChange={(e) => setSelectedCourseId(e.target.value)}
          disabled={coursesLoading}
        >
          <option value="">
            {coursesLoading ? 'Loading departments...' : 'Select a department'}
          </option>
          {courses.map((c) => (
            <option key={c._id} value={c._id}>{c.name} ({c.code})</option>
          ))}
        </select>
      </div>

      {!selectedCourseId && !coursesLoading && (
        <div className="msa-empty-state">
          <p>Select a department above to view and assign its students' thesis faculty.</p>
        </div>
      )}

      {selectedCourseId && detailLoading && (
        <div className="ms-loading">
          <div className="spinner" />
          <p>Loading...</p>
        </div>
      )}

      {selectedCourseId && !detailLoading && (
        <>
          {saveSuccess && <div className="ms-save-alert"><FaCheckCircle /> {saveSuccess}</div>}

          {students.length === 0 ? (
            <div className="msa-empty-state">
              <p>No students found in {selectedCourse?.name || 'this department'}.</p>
            </div>
          ) : teachers.length === 0 ? (
            <div className="msa-empty-state">
              <p>No faculty assigned to {selectedCourse?.name || 'this department'} yet.</p>
            </div>
          ) : (
            <div className="ms-accordion">
              {students.map((student) => {
                const studentId = String(student._id);
                const isExpanded = expandedStudentId === studentId;
                const assigned = assignments[studentId] || [];

                return (
                  <div key={studentId} className={`ms-accordion-card ${isExpanded ? 'expanded' : ''}`}>
                    <div
                      className="ms-accordion-header"
                      onClick={() => setExpandedStudentId(isExpanded ? null : studentId)}
                    >
                      <div className="ms-teacher-info">
                        <div className="ms-teacher-avatar">
                          <FaUserGraduate />
                        </div>
                        <div>
                          <span className="ms-teacher-name">{personName(student)}</span>
                          <span className="ms-teacher-assigned">
                            {assigned.length} guide{assigned.length === 1 ? '' : 's'} assigned
                          </span>
                        </div>
                      </div>
                      <div className="ms-accordion-controls">
                        <button
                          className="ms-btn ms-btn-sm ms-btn-primary"
                          onClick={(e) => { e.stopPropagation(); saveOneStudent(studentId); }}
                          disabled={savingStudentId === studentId}
                        >
                          <FaSave />
                          {savingStudentId === studentId ? 'Saving...' : 'Save'}
                        </button>
                        <span className="ms-accordion-chevron">
                          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </span>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="ms-accordion-body">
                        <TeacherGrid
                          allTeachers={teachers}
                          selectedIds={assigned}
                          onToggle={(id) => toggle(studentId, id)}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ManageThesisFaculty;
